import { createApp } from "vue";
import App from "./App.vue";
import store from "./store"; // vuex
import router from "./router";
import "./assets/css/nucleo-icons.css";
import "./assets/css/nucleo-svg.css";
import "./assets/css/common.css";
import "./assets/scss/pineapple-globals.scss"; // Pineapple 디자인 시스템 글로벌
import VueSweetalert2 from "vue-sweetalert2";
import MaterialDashboard from "./material-dashboard";
import i18n from "./lang/lang.js"; // Language 설정
import CONF from "./Config.js"; // 시스템 구성정보
import common from "@/plugins/common.js";
import axios from "axios";
// FastAPI는 반복 키 형식(`a=1&a=2`)을 기대하므로 axios 기본 배열 직렬화(`a[]=1&a[]=2`)를 변경
axios.defaults.paramsSerializer = { indexes: null };
import Notifications from '@kyvg/vue3-notification';
import MonacoEditor from '@guolao/vue-monaco-editor';
import bootstrap from "bootstrap/dist/js/bootstrap.min.js";

// https://www.npmjs.com/package/vue-loading-overlay
import { LoadingPlugin } from "vue-loading-overlay";
import "vue-loading-overlay/dist/css/index.css";
import { Field, Form as VeeForm, ErrorMessage, defineRule } from "vee-validate";
import { required, email, min } from "@vee-validate/rules";

import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';

// 커스텀 디렉티브 - 권한 체크 제거됨

// vee-validate 규칙 정의
defineRule("required", required);
defineRule("email", email);
defineRule("min", min);

// 앱 인스턴스 생성
const app = createApp(App);

// 글로벌 프로퍼티 설정 (CONF와 같은 전역 설정)
app.config.globalProperties.$CONF = CONF;
// Axios를 글로벌 프로퍼티로 추가
app.config.globalProperties.$axios = axios;
// Bootstrap을 글로벌 프로퍼티로 추가
app.config.globalProperties.$bootstrap = bootstrap;

// vee-validate 컴포넌트 전역 등록
app.component("Field", Field);
app.component("MyForm", VeeForm);
app.component("ErrorMessage", ErrorMessage);
app.component("MonacoEditor", MonacoEditor);

// 플러그인 사용
app.use(store);
app.use(router);
app.use(i18n);
app.use(VueSweetalert2);
app.use(MaterialDashboard);
app.use(common); // Vue에서 this.메소드명 으로 사용
app.use(LoadingPlugin);
app.use(Notifications);

// 커스텀 디렉티브 등록 - 권한 체크 제거됨

app.directive('tooltip', {
    mounted(el, binding) {
        tippy(el, {
            content: binding.value,
            placement: 'top',
            animation: 'fade',
            allowHTML: true,
        });
    },
});

/* Vuex Store에 Router와 Axios를 주입
    아래의 방식으로 vuex에서 router, axios를 사용할 수 있다
    store.$router
    store.$axios
*/
store.$router = router;
store.$axios = app.config.globalProperties.$axios;

// 로그인 화면으로 보낼 때 돌아갈 경로(redirect 쿼리)는 깊은 링크일 때만 붙인다.
// 도메인만 치고 들어온 첫 화면(기본 대시보드)은 로그인 뒤 어차피 그리로 가므로 쿼리를 붙이지 않는다 (2026-10-08)
const DEFAULT_LANDINGS = ["/", "/dashboards/dashboard-default", "/dashboards/sales"];
const toSignin = (to) =>
    DEFAULT_LANDINGS.includes(to.fullPath)
        ? { path: "/authentication/signin" }
        : { path: "/authentication/signin", query: { redirect: to.fullPath } };

// 라우터 전역 가드 설정 (인증 체크만 - 권한 체크 제거됨)
router.beforeEach(async (to, from, next) => {
    // 1. 인증 예외 페이지 — /authentication/* (로그인·회원가입·비밀번호 재설정 및 변형 경로) 와 로그아웃
    // includes 정확 일치 방식은 signin/basic, reset/cover 등 하위 경로가 가드에 걸리는 버그가 있었음
    const isExceptPage = to.path.startsWith("/authentication/") || to.path === "/logout";

    // 2. 인증 체크
    if (!isExceptPage) {
        const token = localStorage.getItem("access_token");

        // 토큰이 없으면 로그인 페이지로
        if (!token || token === "null") {
            return next(toSignin(to));
        }

        // 로그인 상태가 아니면 사용자 정보 로드
        if (!store.state.auth.is_login) {
            try {
                await store.dispatch("auth/getMemberInfo");
            } catch (err) {
                console.error("Failed to fetch user info:", err);
                return next(toSignin(to));
            }
        }

        // 권한 체크 제거됨 - 모든 로그인한 사용자는 모든 페이지에 접근 가능

        // 3. learner(미승인) 계정 접근 제어 — 강사 권한 요청/상태 화면만 허용
        const learnerAllowed = ["/instructor/access-request", "/instructor/access-status", "/logout"];
        const userType = store.state.auth.user_info?.userType;
        if (userType === "learner" && !learnerAllowed.includes(to.path)) {
            return next({ path: "/instructor/access-status" });
        }

        // 3-1. 기관 관리(/organization/*) — 기관 Master 가 아니면 대시보드로.
        //      서버도 403 을 주지만 메뉴가 없는 사용자가 URL 로 들어오는 경우를 화면에서 막는다.
        if (to.path.startsWith("/organization") && !store.getters["organization/isMaster"]) {
            return next({ name: "Sales" });
        }
    }

    // 4. 모든 체크 통과 시 이동 허용
    next();
});

// 앱 마운트
app.mount("#app");

// 운영 모드 설정
if (process.env.NODE_ENV === "production") {
    app.config.devtools = false;
    app.config.debug = false;
    app.config.silent = true;
}